'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
// import { useSwr, mutate } from 'swr'

import useSWRMutation from 'swr/mutation'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'

import { cn } from '@/lib/utils'
import { toast } from 'sonner'

import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardFooter,
} from '@/components/ui/apple-card'

import { TagIcon } from '@/components/ui/icons'

import { Database } from '@/types_db'
import Balancer from 'react-wrap-balancer'
import { FadeIn } from '@/components/FadeIn'
import { Button } from '@/components/ui/button'

import { useUserBookmarks } from '@/data/use-get-user-bookmarks'

import { BookmarkIcon, EyeIcon } from 'lucide-react'
import {
  Tooltip,
  TooltipProvider,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type Course = Partial<Database['public']['Tables']['course']['Row']>

let images = [
  '/loading-v2-8.png',
  '/loading-v2-7.png',
  '/loading-10.png',
  '/loading-v2-5.png',
  '/loading-v2-3.png',
  '/loading-v2-1.png',
  '/loading-1.png',
  '/loading-2.png',
  '/loading-3.png',
  '/loading-4.png',
  '/loading-5.png',
  '/loading-6.png',
  '/loading-8.png',
  '/loading-9.png',
  '/loading-10.png',
]

function shuffleArray(array: string[]) {
  const shuffledArray = [...array] // Create a shallow copy to avoid mutating the original array
  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]]
  }
  return shuffledArray
}

function generateOptimizedPattern(
  totalCourses: number,
  screenSize = 'desktop',
) {
  let dynamicPattern: number[] = []

  // Pre-defined pattern for tablet views
  const tabletPattern = [1, 1, 2]
  // Pre-defined pattern for desktop views
  const desktopPattern = [1, 1, 2, 1, 1, 2]

  if (screenSize === 'tablet') {
    while (dynamicPattern.length < totalCourses) {
      dynamicPattern.push(...tabletPattern)
    }
  } else {
    // desktop
    while (dynamicPattern.length < totalCourses) {
      dynamicPattern.push(...desktopPattern)
    }
  }

  // Trim the array to match the exact number of courses
  dynamicPattern = dynamicPattern.slice(0, totalCourses)

  return dynamicPattern
}

async function sendRequest(
  url,
  { arg }: { arg: { action: string; courseId: string } },
) {
  return fetch(url, {
    method: 'POST',
    body: JSON.stringify(arg),
  }).then((res) => res.json())
}

export function CourseGrid({
  userName,
  user,
  courses,
}: {
  userName: string | null
  courses: Course[]
  user: any
}) {
  const router = useRouter()
  const { bookmarks } = useUserBookmarks(true)
  const { trigger } = useSWRMutation('/api/courses/bookmarks', sendRequest, {
    // populateCache: (newB, all) => {
    //   // filter the list, and return it with the updated item
    //   const bookmarks = all.filter((b) => b !== newB)
    //   return [...bookmarks, newB]
    // },
  }) // replace with your actual fetching function

  const [screenSize, setScreenSize] = useState('desktop')

  const handleToggleBookMark = async (courseId) => {
    const isBookmarked = bookmarks.includes(courseId)

    try {
      // Update the local state immediately and fire the request.
      console.log({ action: isBookmarked ? 'remove' : 'add', courseId })
      await trigger({
        action: isBookmarked ? 'remove' : 'add',
        courseId,
      })

      router.refresh()

      toast.success(
        `Successfully ${isBookmarked ? 'removed' : 'saved'} bookmark.`,
      )
    } catch (e) {
      // If the API errors, the original data will be rolled back by SWR automatically.
      toast.error('Failed to toggle the bookmark.')
    }
  }

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth <= 640) {
        setScreenSize('mobile')
      } else if (window.innerWidth <= 1024) {
        setScreenSize('tablet')
      } else {
        setScreenSize('desktop')
      }
    }

    // Initial set
    handleResize()

    // Listen for resize events
    window.addEventListener('resize', handleResize)

    return () => {
      // Cleanup
      window.removeEventListener('resize', handleResize)
    }
  }, [])
  // Generate the Dynamic Pattern
  const totalCourses = courses ? courses.length : 0
  const dynamicPattern = generateOptimizedPattern(totalCourses)
  const shuffledImages = shuffleArray(images)

  return (
    <div className="mt-4 grid grid-cols-1 gap-4 md:auto-rows-fr md:grid-cols-2 lg:grid-cols-4">
      {courses
        ? courses.map((course: any, idx: number) => {
            const imageIndex = idx % shuffledImages.length
            const spanSize = dynamicPattern[idx] || 1 // Use dynamicPattern here

            if (screenSize === 'mobile') {
              return (
                <div key={course.id}>
                  <Card
                    colorScheme={'stone'}
                    style={{
                      padding: '0px',
                    }}
                    className="relative p-1   shadow-black/30 transition  "
                  >
                    <CardHeader className=" relative mt-2 pb-2">
                      <div className="min-h-[3.6rem]">
                        <CardTitle className="mr-1">
                          <span className="mr-3 line-clamp-3">
                            {course.title}
                          </span>
                        </CardTitle>
                      </div>
                      {user ? (
                        <div className="absolute right-1 top-0">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className="hover:bg-none "
                                  onClick={async () =>
                                    await handleToggleBookMark(course.id)
                                  }
                                >
                                  <BookmarkIcon
                                    className={cn(
                                      bookmarks?.includes(course.id)
                                        ? 'fill-brand-500 text-brand-500'
                                        : 'fill-stone-300/30 stroke-stone-300 dark:fill-stone-700/30',
                                      'h-4 w-4',
                                    )}
                                  />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Bookmark for later</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      ) : null}
                    </CardHeader>
                    <Link href={`/courses/${course.id}`} className="h-full">
                      <CardContent className="">
                        <p className="mt-2 line-clamp-2 text-sm text-stone-500 dark:text-vanta-200 md:line-clamp-2">
                          {course.description}
                        </p>
                      </CardContent>
                      <CardFooter className="mt-auto w-full pb-4 pt-2 ">
                        <figure className=" mt-auto flex w-full items-center   gap-2 space-x-1 pr-1 text-sm font-semibold lowercase tracking-tight">
                          <figcaption className="rounded-lg bg-stone-200/50 px-2 py-1 text-xs font-bold dark:bg-vanta-950 dark:text-stone-300">
                            {course?.ai_settings?.depth}
                          </figcaption>

                          <p className="flex items-center gap-1 text-xs font-bold tracking-wide text-vanta-700 dark:text-stone-200">
                            <TagIcon className="h-4 w-4 fill-brand-400/70 dark:fill-brand-400/90 dark:stroke-vanta-800" />{' '}
                            {course?.tags?.[0]}
                          </p>
                        </figure>
                        <p className="dark:stone-700/30   text-xs font-semibold tracking-wide text-stone-400/50">
                          {course?.view_count ? course.view_count + 1 : ''}
                        </p>
                      </CardFooter>
                    </Link>
                  </Card>
                </div>
              )
            }

            return (
              <div key={course.id}>
                {/* <FadeIn> */}
                <motion.div
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: 0.5,
                    type: 'spring',
                    delay: 0.3,
                  }}
                  key={course.id}
                  className="h-full"
                >
                  <Card
                    colorScheme={'stone'}
                    style={{
                      gridColumnEnd: `span ${spanSize}`,
                      gridRowEnd: `span ${spanSize}`,
                      padding: '0px',
                    }}
                    className="relative p-2   shadow-black/30 transition  "
                  >
                    <CardHeader className=" relative mt-2 pb-2">
                      <div className="min-h-[3.6rem]">
                        <CardTitle className="mr-1">
                          <span className="mr-3 line-clamp-3">
                            {course.title}
                            {/* <Balancer>{course.title}</Balancer> */}
                          </span>
                        </CardTitle>
                      </div>
                      {user ? (
                        <div className="absolute right-1 top-0">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className="hover:bg-none "
                                  onClick={async () =>
                                    await handleToggleBookMark(course.id)
                                  }
                                >
                                  <BookmarkIcon
                                    className={cn(
                                      bookmarks?.includes(course.id)
                                        ? 'fill-brand-500 text-brand-500'
                                        : 'fill-stone-300/30 stroke-stone-300 dark:fill-stone-700/30',
                                      'h-4 w-4',
                                    )}
                                  />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Bookmark for later</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      ) : null}
                    </CardHeader>
                    <Link href={`/courses/${course.id}`} className="h-full">
                      <CardContent className="">
                        <p className="mt-2 line-clamp-1 text-sm text-stone-500 dark:text-vanta-200 md:line-clamp-2">
                          {course.description}
                        </p>
                      </CardContent>
                      <CardFooter className="mt-auto w-full pb-4 pt-2 ">
                        <figure className=" mt-auto flex w-full items-center   gap-2 space-x-1 pr-1 text-sm font-semibold lowercase tracking-tight">
                          <figcaption className="rounded-lg bg-stone-200/50 px-2 py-1 text-xs font-bold dark:bg-vanta-950 dark:text-stone-300">
                            {course?.ai_settings?.depth}
                          </figcaption>

                          <p className="flex items-center gap-1 text-xs font-bold tracking-wide text-vanta-700 dark:text-stone-200">
                            <TagIcon className="h-4 w-4 fill-brand-400/70 dark:fill-brand-400/90 dark:stroke-vanta-800" />{' '}
                            {course?.tags?.[0]}
                          </p>
                        </figure>
                        <p className="dark:stone-700/30   text-xs font-semibold tracking-wide text-stone-400/50">
                          {course.view_count + 1}
                        </p>
                      </CardFooter>
                    </Link>
                  </Card>
                </motion.div>
                {/* </FadeIn> */}
              </div>
            )
          })
        : null}
    </div>
  )
}
